import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { shortDate } from '../format';
import type { AnomalyGroup, ConditionAnomaly, ItemState } from '../types';
import { AnomalyDetailModal } from './AnomalyDetailModal';
import { MaintenanceAccessPanel } from './MaintenanceAccessPanel';
import { ReportAnomalyPanel } from './ReportAnomalyPanel';

const STATE_LABEL: Record<ItemState, string> = {
  VERY_GOOD: 'Très bon', GOOD: 'Bien', AVERAGE: 'Moyen', POOR: 'Mauvais',
};

type Severity = 'ALL' | 'POOR' | 'AVERAGE';

const SEVERITIES: Array<{ value: Severity; label: string }> = [
  { value: 'ALL', label: 'Tous' },
  { value: 'AVERAGE', label: 'Moyen' },
  { value: 'POOR', label: 'Mauvais' },
];

function originLine(item: ConditionAnomaly): string {
  if (item.reportedAt) {
    return `signalé le ${shortDate(item.reportedAt)}${item.reportedBy ? ` par ${item.reportedBy}` : ''}`;
  }
  if (item.observedOn) {
    return `constaté le ${shortDate(item.observedOn)} (${item.observedType === 'ENTRY' ? 'entrée' : 'sortie'})`;
  }
  return 'origine inconnue';
}

/**
 * `basePath` bascule entre l'accès administrateur et l'accès temporaire d'une
 * intervenante (Aurélie) : mêmes actions, deux jeux de routes distingués à
 * l'audit. `allowReopen` reste fermé côté intervenante — annuler une
 * réparation déclarée par erreur est une correction, pas un geste courant.
 */
export function MaintenancePanel({
  basePath = '/api/admin/conditions',
  allowReopen = true,
  showAccessLink = true,
  canReport = true,
}: {
  basePath?: string;
  allowReopen?: boolean;
  showAccessLink?: boolean;
  canReport?: boolean;
} = {}) {
  const [groups, setGroups] = useState<AnomalyGroup[] | null>(null);
  const [openCount, setOpenCount] = useState(0);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [error, setError] = useState('');
  // Filtres d'affichage seulement : ils ne touchent ni les données ni le serveur.
  const [scopeKey, setScopeKey] = useState('ALL');
  const [severity, setSeverity] = useState<Severity>('ALL');
  const [showResolved, setShowResolved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  async function load(): Promise<void> {
    try {
      const result = await api<{ groups: AnomalyGroup[]; openCount: number }>(`${basePath}/anomalies`);
      setGroups(result.groups);
      setOpenCount(result.openCount);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, [basePath]);

  // Recalculé à chaque rendu à partir de `groups` : après enregistrement, la
  // fiche reflète les données fraîchement rechargées sans qu'on ait besoin de
  // la refermer et de la rouvrir.
  const openItem = openItemId
    ? groups?.flatMap((group) => group.items).find((item) => item.id === openItemId) ?? null
    : null;

  const resolvedCount = useMemo(
    () => (groups ?? []).reduce((total, group) => total + group.items.filter((item) => item.resolvedAt).length, 0),
    [groups],
  );

  /**
   * Filtrage purement visuel : rien n'est retiré des données, seulement de
   * l'affichage. Un périmètre vidé par le filtre reste affiché, et un périmètre
   * qui n'a jamais rien eu à signaler aussi : une chambre qui disparaît de la
   * liste se lit comme une chambre oubliée, pas comme une chambre au propre.
   */
  const visibleGroups = useMemo(() => (groups ?? [])
    .filter((group) => scopeKey === 'ALL' || group.key === scopeKey)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => (
        (showResolved || !item.resolvedAt)
        && (severity === 'ALL' || item.state === severity)
      )),
    })), [groups, scopeKey, severity, showResolved]);

  const shownCount = visibleGroups.reduce((total, group) => total + group.items.length, 0);
  const totalCount = (groups ?? []).reduce((total, group) => total + group.items.length, 0);
  // Le premier périmètre qui a quelque chose à montrer s'ouvre d'emblée ;
  // déplier un bloc vide n'apprendrait rien.
  const firstFilledIndex = visibleGroups.findIndex((group) => group.items.length > 0);

  return (
    <section className="panel">
      {openItem && (
        <AnomalyDetailModal
          item={openItem}
          basePath={basePath}
          allowReopen={allowReopen}
          onClose={() => setOpenItemId(null)}
          onSaved={load}
        />
      )}

      {reportOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setReportOpen(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><p className="eyebrow">Hors constat</p><h2>Signaler une anomalie</h2></div>
              <button type="button" className="icon-button" aria-label="Fermer" onClick={() => setReportOpen(false)}>×</button>
            </div>
            <ReportAnomalyPanel embedded basePath={basePath} onReported={load} />
          </div>
        </div>
      )}

      {accessOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setAccessOpen(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><p className="eyebrow">Accès temporaire</p><h2>Lien pour une intervenante</h2></div>
              <button type="button" className="icon-button" aria-label="Fermer" onClick={() => setAccessOpen(false)}>×</button>
            </div>
            <MaintenanceAccessPanel embedded />
          </div>
        </div>
      )}

      <div className="section-heading">
        <div>
          <p className="eyebrow">Entre deux locations</p>
          <h2>Anomalies en cours</h2>
        </div>
        <div className="heading-actions">
          {canReport && <button type="button" className="button compact" onClick={() => setReportOpen(true)}>Signaler une anomalie</button>}
          {showAccessLink && <button type="button" className="button secondary compact" onClick={() => setAccessOpen(true)}>Lien intervenante</button>}
          <span className="count-badge">{openCount}</span>
        </div>
      </div>

      <p className="section-intro compact-intro">
        Tout ce qui n'est pas en très bon état, ou porte une réserve écrite, au dernier constat.
        <span className="muted"> Ouvrez une anomalie pour voir sa photo et déclarer la réparation : sans cette
        déclaration, le locataire entrant hérite des réserves du précédent.</span>
      </p>

      {error && <div className="inline-message error" role="alert">{error}</div>}
      {!groups && !error && <p className="muted">Chargement…</p>}

      {groups && groups.length > 0 && (
        <div className="filter-row">
          <select value={scopeKey} onChange={(event) => setScopeKey(event.target.value)} aria-label="Filtrer par périmètre">
            <option value="ALL">Tous les périmètres</option>
            {groups.map((group) => <option key={group.key} value={group.key}>{group.label}</option>)}
          </select>
          <div className="segmented severity-filter">
            {SEVERITIES.map((entry) => (
              <button
                type="button"
                key={entry.value}
                className={severity === entry.value ? 'active' : ''}
                onClick={() => setSeverity(entry.value)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          {/* Toujours visible, même à zéro : sans elle, la première anomalie
              déclarée réparée disparaîtrait sans explication et passerait pour
              une perte de donnée. */}
          {(
            <label className={resolvedCount > 0 ? 'check-field resolved-toggle' : 'check-field resolved-toggle muted-toggle'}>
              <input
                type="checkbox"
                disabled={resolvedCount === 0}
                checked={showResolved}
                onChange={(event) => setShowResolved(event.target.checked)}
              />
              <span>
                {resolvedCount > 0
                  ? `Afficher les ${resolvedCount} déjà réparés`
                  : 'Aucune réparation déclarée pour l’instant'}
              </span>
            </label>
          )}
        </div>
      )}

      {groups && totalCount === 0 && (
        <div className="empty-state">Aucune anomalie en cours : le logement est au propre dans le référentiel.</div>
      )}
      {groups && totalCount > 0 && shownCount === 0 && (
        <div className="empty-state">Aucune anomalie ne correspond à ce filtre.</div>
      )}

      <div className="anomaly-groups">
        {visibleGroups.map((group, index) => {
          const openHere = group.items.filter((item) => !item.resolvedAt).length;
          const poorHere = group.items.filter((item) => item.state === 'POOR' && !item.resolvedAt).length;
          return (
            <details
              className="room-section"
              key={group.key}
              open={group.items.length > 0 && (scopeKey !== 'ALL' || index === firstFilledIndex)}
            >
              <summary className="group-summary">
                <span className="room-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="room-name">
                  <strong>{group.label}</strong>
                  <small>{group.items.length} élément(s) affiché(s)</small>
                </span>
                {poorHere > 0 && <span className="status alert-pill">{poorHere} mauvais</span>}
                <span className={openHere > 0 ? 'status draft' : 'status signed'}>
                  {openHere > 0 ? `${openHere} à traiter` : 'rien à traiter'}
                </span>
                <span className="chevron" aria-hidden="true">›</span>
              </summary>
              <div className="room-items">
                {group.items.length === 0 && (
                  <p className="muted empty-line">Rien à traiter dans ce périmètre.</p>
                )}
                {group.items.map((item) => (
                  <button
                    type="button"
                    className={item.resolvedAt ? 'anomaly-row clickable resolved' : 'anomaly-row clickable'}
                    key={item.id}
                    onClick={() => setOpenItemId(item.id)}
                  >
                    <div className="anomaly-main">
                      <strong>{item.roomLabel} · {item.itemLabel}</strong>
                      <p className="record-line muted">
                        <span>{STATE_LABEL[item.state]}{item.comment && ` — ${item.comment}`}</span>
                        <span>{originLine(item)}</span>
                      </p>
                      {item.resolvedAt && (
                        <p className="record-line">
                          <span className="status signed">
                            Réparé{item.performedOn ? ` le ${shortDate(item.performedOn)}` : ''}
                            {item.performedBy ? ` par ${item.performedBy}` : ''}
                            {item.repairPhotoUrls.length === 0 ? ' · sans photo' : ''}
                          </span>
                        </p>
                      )}
                    </div>
                    <span className="anomaly-chevron" aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            </details>
          );
        })}
      </div>

      {groups && groups.length > 0 && (
        <p className="muted list-count">{shownCount} anomalie(s) affichée(s) sur {openCount + resolvedCount} au total.</p>
      )}
    </section>
  );
}
